import { useEffect, useMemo, useRef, useState } from "react";
import { DEFAULT_LIMIT_ITEMS } from "constants/data";
import { PaginationParamType } from "type";
import { getUserType } from "util/auth";
import { USER_TYPE_LIST } from "constants/user-type";
import { PRICE_SETTING_TIRE } from "features/proc_fee/types/tire";
import { getPriceSettingMethod } from "features/proc_fee/api/tireDetailBrandApi";
import { fetchWheelPrice, updateWheelPrice, updateStorePriceSettingApi } from "../api/wheelPriceApi";
import { WheelPriceEditable, StorePriceSettingWheelParams, UpdateWheelPriceRequest } from "../types/wheel-price";

/**
 *
 */
export default function useWheelPriceSetting() {
  const userType = getUserType();
  const [wheelPriceTable, setWheelPriceTable] = useState<WheelPriceEditable[]>([]);
  const [queryParams, setQueryParams] = useState<PaginationParamType>({
    limit: DEFAULT_LIMIT_ITEMS,
    offset: 1,
  });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [storePriceSetting, setStorePriceSetting] = useState<number>(PRICE_SETTING_TIRE.SELLING);
  /** Only call mst/store/update when the user explicitly changes the dropdown */
  const isUserChange = useRef(false);
  const totalPage = useMemo(() => Math.ceil(total / DEFAULT_LIMIT_ITEMS), [total]);

  const fetchData = async () => {
    setLoading(true);
    const data = await fetchWheelPrice(queryParams);
    const isHeadquarters = userType === USER_TYPE_LIST.headquarters.value;

    if (data) {
      const prices = data.list.map<WheelPriceEditable>((price) => ({
        brand_cd: price.brand_cd,
        maker_name: price.maker_name || "",
        brand_name: price.brand_name || "",
        fixed_discount: price.fixed_discount ?? ("" as unknown as number),
        sale_price_rate_cost: price.sale_price_rate_cost ?? ("" as unknown as number),
        fixed_discount_store_login_edit_flag: price.fixed_discount_store_login_edit_flag,
        sale_price_rate_cost_store_login_edit_flag:
          price.sale_price_rate_cost_store_login_edit_flag,
        priceRateChanged: false,
        fixedDiscountChanged: false,
        titleEditable: isHeadquarters ? "true" : "false",
      }));
      setWheelPriceTable((prev) => {
        const newPrices = queryParams.offset > 1 ? [...prev, ...prices] : prices;
        return newPrices;
      });
      setTotal(data.total);
    }
    setLoading(false);
  };

  const normalizeValue = (key: keyof WheelPriceEditable, value: string) => {
    const includeNumberParseKey = ["fixed_discount", "sale_price_rate_cost"];
    if (includeNumberParseKey.includes(key)) {
      return value === "" ? 0 : +value;
    }
    return value;
  };

  const handleUpdatePrice = async (
    index: number,
    key: keyof WheelPriceEditable,
    value: string,
    onUpdateError?: (originalValue: string) => void,
  ) => {
    const target = wheelPriceTable[index];
    if (!target) return;

    const numericKeys = ["fixed_discount", "sale_price_rate_cost"];
    const isNumeric = numericKeys.includes(key);
    const originalValue = target[key];

    const isCleared =
      isNumeric &&
      value === "" &&
      originalValue !== "" &&
      originalValue !== null &&
      originalValue !== undefined;

    const shouldUpdate = isCleared || value !== String(target[key]);

    if (!shouldUpdate) return;

    try {
      const payload: UpdateWheelPriceRequest = isCleared
        ? {
            brand_cd: target.brand_cd,
            [key]: 0,
            column_delete_flag: 1,
          }
        : {
            brand_cd: target.brand_cd,
            [key]: key === "fixed_discount" ? normalizeValue(key, value) : String(value),
          };
      await updateWheelPrice(payload);
      setWheelPriceTable((prev) => {
        if (!prev[index]) return prev;
        const newPrices = [...prev];
        newPrices[index] = {
          ...newPrices[index],
          [key]: isCleared ? ("" as unknown as number) : normalizeValue(key, value),
        };
        return newPrices;
      });
      fetchData();
    } catch (error) {
      if (onUpdateError) {
        onUpdateError(String(wheelPriceTable[index][key]));
      }
      console.error("Error updating wheel price:", error);
    }
  };

  const onLoadMore = async () => {
    if (queryParams.offset + 1 > totalPage) return;
    setQueryParams((prev) => ({
      ...prev,
      offset: prev.offset + 1,
    }));
  };

  const updateStorePriceSetting = async () => {
    const storeCd = localStorage.getItem("shop_cd") || "";
    const updateData: StorePriceSettingWheelParams = {
      store_cd: storeCd,
      mst_store_price_setting_wheel: storePriceSetting,
    };
    await updateStorePriceSettingApi(updateData);
  };

  useEffect(() => {
    if (queryParams.offset > 1) {
      fetchData();
    }
  }, [queryParams.offset]);

  useEffect(() => {
    fetchData();
  }, []);

  // Load current ホイールセット時の売価計算方法 on first page load
  useEffect(() => {
    const loadPriceSettingMethod = async () => {
      const storeCd = localStorage.getItem("shop_cd") || "";
      if (!storeCd) return;
      try {
        const { list } = await getPriceSettingMethod(storeCd);
        if (list?.length > 0) {
          const value = list[0].mst_store_price_setting_wheel;
          if (userType === USER_TYPE_LIST.store.value) {
            setStorePriceSetting(value !== 0 ? PRICE_SETTING_TIRE.COST : PRICE_SETTING_TIRE.SELLING);
          } else {
            setStorePriceSetting(value !== null ? Number(value) : PRICE_SETTING_TIRE.SELLING);
          }
        }
      } catch (error) {
        console.error("Error loading price setting method:", error);
      }
    };
    loadPriceSettingMethod();
  }, []);

  useEffect(() => {
    if (!isUserChange.current) return;
    isUserChange.current = false;
    if (localStorage.getItem("shop_cd")) updateStorePriceSetting();
  }, [storePriceSetting]);

  const handleStorePriceSettingChange = (value: number) => {
    isUserChange.current = true;
    setStorePriceSetting(value);
  };

  return { wheelPriceTable, handleUpdatePrice, onLoadMore, loading, storePriceSetting, handleStorePriceSettingChange };
}
